import { computed, reactive, ref } from 'vue'
import * as api from './api'
import {
    AddMemberRequest, AddMemberRequestWithoutPassword,
    addMemberWithoutAuthenticationProfile,
    addMemberWithPasswordProfile
} from '@/modules/members/api'
import { MemberProfile } from '@/modules/members/types'
import { OrganizationsSimplifiedListModel, ValidityResultType } from '@/modules/organizations/types'
import { SimpleListFilterParameters } from '@/views/_shared/search-list/types'
export * from './types'

const organizations = ref<api.GetOrganizationsListResponse['results']>([])
const organizationsCount = ref(0)
const organizationsSimplified = ref<api.GetOrganizationsSimplifiedListResponse>([])
const organizationMembers = ref<MemberProfile[]>([])
const organizationMembersCount = ref<number>()
const supervisors = ref<api.GetSupervisorsListResponse>([])

export const organizationsFilter = reactive<SimpleListFilterParameters>({
    search: '',
    sortBy: null,
    limit: 10,
    offset: 0
})

export const organizationMembersFilter = reactive<SimpleListFilterParameters>({
    search: '',
    sortBy: null,
    limit: 10,
    offset: 0
})

const getOrganizations = () =>
    api.getOrganizationsList(
        organizationsFilter.search,
        organizationsFilter.sortBy || 'id desc',
        organizationsFilter.limit,
        organizationsFilter.offset
    ).then(
        organizationsList => {
            organizationsCount.value = organizationsList.count
            organizations.value = organizationsList.results
        }
    )

const getOrganizationsSimplified = () =>
    api.getOrganizationsSimplifiedList().then(
        organizationsSimplifiedList => {
            organizationsSimplified.value = organizationsSimplifiedList
        }
    )

const addOrganization = (payload: api.AddOrganizationRequest) =>
    api.addOrganization(payload).then(
        async () => {
            await getOrganizations()
        }
    )

const getOrganizationMembers = (id: string) => {
    console.log(organizationMembersFilter)
    return api.getOrganizationMembersList(
        id,
        organizationMembersFilter.search,
        organizationMembersFilter.sortBy || 'memberId desc',
        organizationMembersFilter.limit,
        organizationMembersFilter.offset
    ).then(
        organizationMembersList => {
            organizationMembers.value = organizationMembersList.results
            organizationMembersCount.value = organizationMembersList.count
        }
    )
}

const getSupervisors = (id: string, memberId: string) =>
    api.getSupervisors(id, memberId).then(
        supervisorsList => {
            supervisors.value = supervisorsList
        }
    )

/* Organization agreements */
const getOrganizationAgreementsSimplified = (organizationId: string) =>
    api.getOrganizationAgreementsSimplified(organizationId)

/* Organization members */

const addMemberToOrganization = (memberId: string, organizationId: string, req: api.AddMemberToOrganizationRequest) =>
    api.addMemberToOrganization(memberId, organizationId, req)

const addMemberInOrganizationWithoutAuthenticationProfile = async (organizationId: string, payload: AddMemberRequestWithoutPassword) => {
    const member = await addMemberWithoutAuthenticationProfile(payload) as { memberId: string }
    await addMemberToOrganization(member.memberId, organizationId,
        { memberId: member.memberId, organizationId: organizationId })
    await getOrganizationMembers(organizationId)
    return member
}

const addMemberInOrganizationWithPasswordProfile = async (organizationId: string, payload: AddMemberRequest) => {
    const member = await addMemberWithPasswordProfile(payload) as { memberId: string }
    await addMemberToOrganization(member.memberId, organizationId,
        { memberId: member.memberId, organizationId: organizationId })
    await getOrganizationMembers(organizationId)
    return member
}

const deleteMemberFromOrganization = (memberId: string, organizationId: string, req: api.DeleteMemberFromOrganizationRequest) =>
    api.deleteMemberFromOrganization(memberId, organizationId, req).then(async () => {
        await getOrganizationMembers(organizationId)
    })

/* Organization */

const withOrganizationUpdate = async (organizationId: string, callback: () => void) => {
    await callback()
    await getOrganization(organizationId)
}

const getOrganization = async (id: string) => {
    const organization = await api.getOrganization(id)
    if (organizations.value.length) {
        const organizationIndex = organizations.value.findIndex(value => value.id === id)
        organizations.value[organizationIndex] = organization
    }
    return organization
}

const updateOrganizationName = (id: string, payload: api.UpdateOrganizationNameRequest) =>
    withOrganizationUpdate(id, () => api.updateOrganizationName(id, payload))

const updateOrganizationContactPerson = (id: string, payload: api.UpdateContactPersonRequest) =>
    withOrganizationUpdate(id, () => api.updateOrganizationContactPerson(id, payload))

const updateOrganizationLogoUrl = (id: string, payload: api.UpdateOrganizationLogoUrlRequest) =>
    withOrganizationUpdate(id, () => api.updateOrganizationLogoURL(id, payload))

const updateOrganizationBusinessId = (id: string, payload: api.UpdateOrganizationBusinessIdRequest) =>
    withOrganizationUpdate(id, () => api.updateOrganizationBusinessId(id, payload))

/* Organization member profile */

const getMemberOrganizationProfile = async (organizationId: string, memberId: string) => {
    return await api.getOrganizationMemberProfile(organizationId, memberId)
}
const withMemberOrganizationProfileUpdate = async (organizationId: string, memberId: string, callback: () => void) => {
    await callback()
    await getMemberOrganizationProfile(organizationId, memberId)
}

const updateOrganizationMemberPhone = (id: string, memberId: string, payload: api.UpdateMemberPhoneRequest) =>
    withMemberOrganizationProfileUpdate(id, memberId,
        () => api.updateOrganizationMemberPhone(id, memberId, payload)
    )

const updateOrganizationMemberHierarchyAssignment = (id: string, memberId: string, payload: api.UpdateMemberHierarchyAssignmentRequest) =>
    withMemberOrganizationProfileUpdate(id, memberId,
        () => api.updateOrganizationMemberHierarchyAssignment(id, memberId, payload)
    )

const updateOrganizationMemberSupervisor = (id: string, memberId: string, payload: api.UpdateMemberSupervisorRequest) =>
    withMemberOrganizationProfileUpdate(id, memberId,
        () => api.updateOrganizationMemberSupervisor(id, memberId, payload)
    )

const removeOrganizationMemberSupervisor = (id: string, memberId: string, payload: api.RemoveMemberSupervisorRequest) =>
    withMemberOrganizationProfileUpdate(id, memberId,
        () => api.removeOrganizationMemberSupervisor(id, memberId, payload)
    )

const updateOrganizationMemberReporting = (id: string, memberId: string, payload: api.UpdateMemberReportingRequest) =>
    withMemberOrganizationProfileUpdate(id, memberId,
        () => api.updateOrganizationMemberReporting(id, memberId, payload)
    )

const updateMembersProfiles = async (organizationId: string, file: File) => {
    const response = await api.updateMembersProfiles(organizationId, file)
    await getOrganizationMembers(organizationId)
    return response
}

const updateOrganizationIndustry = (id: string, payload: api.UpdateOrganizationIndustryRequest) =>
    withOrganizationUpdate(id, () => api.updateSingleOrganizationIndustry(id, payload))

const turnOnSsoReportingDimensions = (id: string) =>
    withOrganizationUpdate(id, () => api.turnOnSsoReportingDimensions(id, { organizationId: id }))

const turnOffSsoReportingDimensions = (id: string) =>
    withOrganizationUpdate(id, () => api.turnOffSsoReportingDimensions(id, { organizationId: id }))
export function useOrganizationModule () {
    return {
        addMemberInOrganizationWithPasswordProfile,
        addMemberInOrganizationWithoutAuthenticationProfile,
        addOrganization,
        checkBusinessIdIsValid: api.checkBusinessIdIsValid,
        checkBusinessIdIsAvailable: api.checkBusinessIdIsAvailable,
        deleteMemberFromOrganization,
        getMemberOrganizationProfile,
        getOrganization,
        getOrganizationAgreementsSimplified,
        getOrganizationMembers,
        getOrganizations,
        getOrganizationsSimplified,
        getSupervisors,
        organizationMembers: computed(() => organizationMembers.value),
        organizationMembersCount: computed(() => organizationMembersCount.value),
        organizations: computed(() => organizations.value),
        organizationsCount: computed(() => organizationsCount.value),
        organizationsFilter,
        organizationsSimplified: computed(() => organizationsSimplified.value),
        organizationsSimplifiedByOrganizationId: computed(() => organizationsSimplified.value.reduce((list, organization) => {
            list.set(organization.id, organization)
            return list
        }, new Map<string, OrganizationsSimplifiedListModel>())),
        supervisors: computed(() => supervisors.value),
        updateOrganizationContactPerson,
        updateOrganizationLogoUrl,
        updateOrganizationBusinessId,
        updateOrganizationMemberHierarchyAssignment,
        updateOrganizationMemberPhone,
        updateOrganizationMemberReporting,
        updateOrganizationMemberSupervisor,
        removeOrganizationMemberSupervisor,
        updateOrganizationName,
        ValidityResultType,
        updateMembersProfiles,
        updateOrganizationIndustry,
        turnOnSsoReportingDimensions,
        turnOffSsoReportingDimensions
    }
}
